import { useQuery, useMutation } from 'react-query';
import { useCurrentAddressInfo, useUpdateCurrentAddressInfo } from '@/hooks';
import message from 'antd/es/message';
import { request, queryClient, L2TxReqParams, toastError } from '@/utils';

export enum PAID_FEATURE {
  VIP = 'vip',
  TIME_MACHINE = 'time_machine',
  HISTORY_ANALYSIS = 'history_analysis',
  NON_NFT_AVATAR = 'non_nft_avatar',
  PROFILE_BANNER = 'profile_banner',
  FOLLOW_3000 = 'follow_3000',
  CHANGE_VIEW = 'change_view',
  SUMMARY_VIEW = 'summary_view',
}

export const useQueryPaidFeatureList = () => {
  return useQuery(['/paid_feature/list'], () =>
    request
      .get<{
        paid_feature_list: PaidFeatureItem[];
      }>('/paid_feature/list')
      .then(res => res.paid_feature_list),
  );
};

export const useQueryPaidFeatureCanSubscribe = (id: string, duration: number, userId?: string) => {
  const url = '/paid_feature/can_subscribe';
  const { address } = useCurrentAddressInfo();

  return useQuery(
    [url, id, duration, address],
    () => {
      return request.get<{
        can_subscribe: boolean;
        msg?: boolean;
      }>(url, {
        id,
        duration,
        user_id: userId!,
      });
    },
    {
      enabled: !!userId,
    },
  );
};

const USER_PAID_FEATURE_LIST_URL = '/user/paid_feature_list';

const invalidateUserPaidListQuery = () => {
  queryClient.invalidateQueries([USER_PAID_FEATURE_LIST_URL]);
};

const usePaidFeatures = (target?: string) => {
  return useQuery(
    [USER_PAID_FEATURE_LIST_URL, target],
    () =>
      request
        .get<{
          paid_feature_list: {
            id: PAID_FEATURE;
            name: string;
            expired_at: number;
          }[];
        }>(USER_PAID_FEATURE_LIST_URL, {
          id: target!,
        })
        .then(res => res.paid_feature_list),
    {
      enabled: !!target,
    },
  );
};

export const usePaidFeatureAvailability = (id?: PAID_FEATURE) => {
  const { address, userInfo } = useCurrentAddressInfo();

  const { data, isLoading } = usePaidFeatures(!id || userInfo?.is_vip ? undefined : address);

  return {
    isLoading,
    data:
      userInfo?.is_vip ||
      (data && Boolean(data?.find(item => [PAID_FEATURE.VIP, id].includes(item.id)))),
  };
};

export const useQueryUserPaidList = (otherUserId?: string, enabled = true) => {
  const { address } = useCurrentAddressInfo();

  return usePaidFeatures(enabled ? otherUserId || address : undefined);
};

export const useSubscribePaidFeature = () => {
  const update = useUpdateCurrentAddressInfo();

  return useMutation(
    ({
      id,
      duration,
      tx,
      sendToUserId,
    }: {
      id: string;
      duration: number;
      tx?: L2TxReqParams;
      sendToUserId?: string;
    }) => {
      return request.post<{ is_success: boolean }>('/paid_feature/subscribe', {
        id,
        duration,
        tx,
        user_id: sendToUserId,
      });
    },
    {
      onSuccess(res, { id }) {
        if (!res?.is_success) {
          return;
        }

        if (id === PAID_FEATURE.VIP) {
          update(pre => {
            const next = { ...pre };
            if (next.userInfo?.desc) {
              next.userInfo = {
                ...next.userInfo,
                is_vip: true,
              };
            }
            return next;
          });
        }

        invalidateUserPaidListQuery();
        message.success('Successfully');
      },
      onError(e) {
        toastError(e || 'Failed to subscribe');
      },
    },
  );
};
